import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { UserService } from 'src/services/api/UserService';
import { Check, Mail, Lock } from 'lucide-react';
import { trackEvent } from 'src/utils/analytics';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateEmail = (emailId) => {
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return regex.test(emailId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    trackEvent('form_submit', 'Forgot Password', 'email');

    // Validate email
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    try {
      setIsLoading(true);

      // Call forgot password API
      const response = await UserService.forgotPassword({
        email: email.trim(),
      });

      // If API explicitly indicates success
      if (response?.success === true) {
        trackEvent('form_success', 'Forgot Password', 'email');
        setSuccess(true);
        return;
      }

      // If status is processed, treat as success
      if (response.status === 'processed') {
        trackEvent('form_success', 'Forgot Password', 'email');
        setSuccess(true);
        return;
      }

      // If status is present but not processed, treat as error
      if (response.status) {
        trackEvent('form_error', 'Forgot Password', 'email');
        setError(`Request failed with status: ${response.status}`);
        return;
      }

      // Fallback error
      trackEvent('form_error', 'Forgot Password', 'email');
      setError('Failed to process request. Please try again.');
    } catch (err) {
      console.error('Forgot password error:', err);
      trackEvent('form_error', 'Forgot Password', 'email');
      setError(err.message || 'Failed to process request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className=" h-screen flex  justify-center bg-gradient-to-b from-blue-50 via-white to-blue-50">
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm border-blue-100 p-6 h-fit mt-6">
        <div className="w-full max-w-lg">
          {success ? (
            <div className="text-center max-w-xs mx-auto">
              <div className="mb-4 flex justify-center">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <h3 className="text-base font-semibold mb-2">Link sent</h3>
              <p className="text-sm text-gray-600 mb-6">
                If this email exists in our system,
                <span className="font-medium">{email}</span> you will receive
                reset instructions.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/join-us')}
                  className="h-9"
                >
                  Done
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9"
                  onClick={() => setSuccess(false)}
                >
                  Resend
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <h1 className="text-base font-semibold">
                    Reset your password
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    We’ll email you a link to set a new password
                  </p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="mt-4">
                <div>
                  <span className="block text-xs font-medium text-gray-700 mb-1 text-left">
                    Email address
                  </span>
                  <Input
                    id="email"
                    aria-label="Email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setError('');
                      setEmail(e.target.value);
                    }}
                    disabled={isLoading}
                    className={`h-9 ${error ? 'border-red-500' : ''}`}
                  />
                  {error && (
                    <p className="mt-1 text-sm text-red-500 text-left">
                      {error}
                    </p>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 my-4">
                  <Lock className="h-3.5 w-3.5 text-blue-600" />
                  Your email is used only to send the reset link.
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="w-full h-9 bg-primary hover:bg-ah-brand-primary-hover"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        Sending...
                      </div>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
